import { ApiError, CancelablePromise, CancelError } from 'api-clients/monolith';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  buildErrorState,
  buildLoadingState,
  buildReadyState,
  buildreloadState,
  ReloadableResultStatus,
} from '../api/resultStatus';

export interface UseApiService<T, E> {
  result: ReloadableResultStatus<T, E>;
  refetch: () => void;
  cancel: () => void;
}

export interface UseApiServiceOptions {
  /** When false, the request is not made. Default true. */
  enabled?: boolean;
}

/**
 * Should be kept in sync with the copy of `applicant-ui`'s `useApiService`
 * hook to allow both to reference a single `:packages/react-hooks` or similar
 *
 * @see {@link :packages/applicant-ui/app/hooks/useApiService.ts}
 */
export const useApiService = <T, E = unknown>(
  // This service should be memoized before passed in, for example by using `useCallback`.
  service: () => CancelablePromise<T>,
  options?: UseApiServiceOptions,
): UseApiService<T, E> => {
  const enabled = options?.enabled !== false;
  const [result, setResult] =
    useState<ReloadableResultStatus<T, E>>(buildLoadingState());
  const [queryKey, setQueryKey] = useState(false);
  const promiseRef = useRef<CancelablePromise<T> | null>(null);

  const setLoadingOrReloading = useCallback(() => {
    setResult(currentResult => {
      // Has loaded once succesfully and has valid cached data
      if (currentResult.status === 'ready') {
        return buildreloadState<T>(currentResult.data);
      }
      if (currentResult.status !== 'loading') {
        return buildLoadingState();
      }
      return currentResult;
    });
  }, []);

  const cancel = useCallback(() => {
    if (promiseRef.current) {
      promiseRef.current.cancel();
      promiseRef.current = null;
    }
  }, []);

  const refetch = useCallback(() => {
    setQueryKey(currentKey => !currentKey);
  }, []);

  useEffect(() => {
    if (!enabled) {
      return () => {};
    }
    setLoadingOrReloading();
    const promise = service();
    promiseRef.current = promise;

    promise
      .then(data => {
        setResult(buildReadyState(data));
        promiseRef.current = null;
      })
      .catch((e: Error) => {
        if (e instanceof CancelError) {
          return;
        }
        if (e instanceof ApiError) {
          setResult(
            buildErrorState((e.body as E) || (e.statusText as unknown as E)),
          );
          promiseRef.current = null;
          return;
        }
        setResult(buildErrorState());
        promiseRef.current = null;
      });
    return () => promise.cancel();
  }, [enabled, service, queryKey, setLoadingOrReloading]);

  return { result, refetch, cancel };
};
